import { SetupScreen } from '@cortex/feature-client-management/ui/web/screens/SetupScreen';
import { PmChatScreen } from '@cortex/feature-pm-chat/ui/web/screens/PmChatScreen';
import { ChatScreen } from '@cortex/feature-resident-assistant/ui/web/screens/ChatScreen';
import { LoginScreen } from '@cortex/feature-resident-assistant/ui/web/screens/LoginScreen';
import { chatPublicStart } from '@cortex/web-api/clients/web/api';
import { useEffect, useState } from 'react';

/**
 * apps/web routes by URL path AND fragment:
 *   /             → resident chat (public-start when enabled, otherwise
 *                   LoginScreen → ChatScreen, v1.0)
 *   /setup        → PM onboarding wizard (SetupScreen, v1.2 PR2+)
 *   anything else → falls through to chat (no 404 — nginx already 404s for
 *                   un-mounted backend paths)
 *
 * No router library — three states, one path check, one verified flag.
 * Same opinion as apps/admin/App.tsx: small surface, deep links not a
 * requirement, browser back/forward works fine via the existing useState.
 */

export function App() {
  const isPmChat = window.location.pathname.startsWith('/pm-chat');
  const isSetup = window.location.pathname.startsWith('/setup');
  // Server-side cookie does the actual auth; this is just a UI hint about which
  // screen to render. /chat/messages will 401 if the cookie is gone or expired,
  // and ChatScreen calls onLoggedOut to retry public-start (demo/sandbox) or
  // drop back to LoginScreen (client tenant origins).
  const [authState, setAuthState] = useState<'checking' | 'login' | 'chat'>('checking');
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);

  useEffect(() => {
    if (isSetup || isPmChat) return;
    // Read the retry token so the exhaustive-deps linter understands it is an
    // intentional trigger, not an accidental extra dependency.
    void bootstrapAttempt;
    let cancelled = false;
    async function bootstrapPublicChat() {
      setAuthState('checking');
      try {
        await chatPublicStart();
        if (!cancelled) setAuthState('chat');
      } catch {
        if (!cancelled) setAuthState('login');
      }
    }
    void bootstrapPublicChat();
    return () => {
      cancelled = true;
    };
  }, [isSetup, isPmChat, bootstrapAttempt]);

  if (isPmChat) {
    return <PmChatScreen />;
  }

  // Setup wizard is its own surface — different cookie (cortex_setup_session
  // path-scoped /pm/setup), different lifecycle (one-time invite consumption).
  if (isSetup) {
    return <SetupScreen />;
  }

  if (authState === 'checking') {
    return (
      <div className="app">
        <main className="app-main">
          <div className="center-stack">
            <p className="field-help">connecting to chat…</p>
          </div>
        </main>
      </div>
    );
  }

  return authState === 'chat' ? (
    <ChatScreen
      onLoggedOut={() => {
        setAuthState('checking');
        setBootstrapAttempt((n) => n + 1);
      }}
    />
  ) : (
    <LoginScreen onVerified={() => setAuthState('chat')} />
  );
}
